import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** @extends {Component<{count: HTMLElement}>} */
class RbfCartCount extends Component {
  requiredRefs = ['count'];
  #listeners;
  #request;
  #revision = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, { signal: this.#listeners.signal });
    window.addEventListener('pageshow', this.onPageShow, { signal: this.#listeners.signal });
    if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') this.reconcile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    this.#revision += 1;
  }

  /** @param {Event & {promise?: Promise<{cart?: {totalQuantity?: number}}>}} event */
  onCartUpdate = async (event) => {
    const revision = ++this.#revision;
    this.#request?.abort();
    try {
      const result = await event.promise;
      if (!this.isConnected || revision !== this.#revision) return;
      const count = result?.cart?.totalQuantity;
      if (Number.isInteger(count) && count >= 0) this.refs.count.textContent = String(count);
      else await this.reconcile();
    } catch {
      // A rejected cart change must not replace the last confirmed count.
    }
  };

  /** @param {PageTransitionEvent} event */
  onPageShow = (event) => {
    if (event.persisted) this.reconcile();
  };

  async reconcile() {
    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;
    const revision = this.#revision;
    try {
      const response = await fetch(`${this.dataset.cartUrl}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (controller.signal.aborted || !this.isConnected || revision !== this.#revision) return;
      if (Number.isInteger(cart.item_count) && cart.item_count >= 0) this.refs.count.textContent = String(cart.item_count);
    } catch {
      // Preserve the server-rendered count when a refresh is unavailable.
    }
  }
}

if (!customElements.get('rbf-cart-count')) customElements.define('rbf-cart-count', RbfCartCount);
